import { shouldEncrypt } from "./mediaEncryption";
import type { DeviceFamily } from "./platformCompat";

export type MediaPushMode = "adb" | "agent";

export type MediaPushTarget = DeviceFamily | "desktop";

export interface MediaAdditionalFile {
  name: string;
  s3Url: string;
  sizeInBytes: number;
}

export interface MediaManifestItem {
  id: number;
  type: string;
  s3Url: string | null;
  s3UrlPreset2: string | null;
  s3UrlPreset3: string | null;
  s3UrlPreset4: string | null;
  s3UrlImagePreset5: string | null;
  s3UrlImagePreset6: string | null;
  updatedAt: string | null;
  sizeInBytes: number;
  sizeInBytesPreset2: number | null;
  sizeInBytesPreset3: number | null;
  sizeInBytesPreset4: number | null;
  sizeInBytesImagePreset5: number | null;
  sizeInBytesImagePreset6: number | null;
  name: string | null;
  additionalFiles: MediaAdditionalFile[];
}

export interface MediaFileToPush {
  url: string;
  filename: string;
  sizeInBytes: number;
  encrypt: boolean;
}

export function extractFilename(url: string): string {
  const withoutQuery = url.split("?")[0];
  return withoutQuery.split("/").pop() || "unknown";
}

function presetFile(
  media: MediaManifestItem,
  url: string | null,
  sizeInBytes: number | null,
): MediaFileToPush | null {
  if (!url) return null;
  const filename = extractFilename(url);
  return {
    url,
    filename,
    sizeInBytes: sizeInBytes || 0,
    encrypt: shouldEncrypt(media.type, filename),
  };
}

type PresetUrlKey =
  | "s3Url"
  | "s3UrlPreset2"
  | "s3UrlPreset3"
  | "s3UrlPreset4"
  | "s3UrlImagePreset5"
  | "s3UrlImagePreset6";

type DeviceRow = PresetUrlKey[][];

const PRESET_ORDER: PresetUrlKey[] = [
  "s3Url",
  "s3UrlPreset2",
  "s3UrlPreset3",
  "s3UrlPreset4",
  "s3UrlImagePreset5",
  "s3UrlImagePreset6",
];

const QUEST_3: DeviceRow = [["s3UrlPreset2"], ["s3UrlImagePreset6"]];
const QUEST_2: DeviceRow = [["s3UrlPreset2"], ["s3UrlImagePreset5"]];
const PICO_4: DeviceRow = [["s3UrlPreset3", "s3UrlPreset2"], ["s3UrlImagePreset6"]];
const PICO_NEO_3: DeviceRow = [["s3UrlPreset2"], ["s3UrlImagePreset5"]];
const ANDROID_MOBILE: DeviceRow = [["s3UrlPreset2"], ["s3UrlImagePreset5"]];
const DESKTOP: DeviceRow = [["s3UrlPreset2"]];
const DESKTOP_7K_TEMPLATE: DeviceRow = [["s3UrlPreset4"]];

const DEVICE_ROWS: Record<MediaPushTarget, DeviceRow[]> = {
  quest: [QUEST_3, QUEST_2],
  pico: [PICO_4, PICO_NEO_3],
  android: [ANDROID_MOBILE],
  desktop: [DESKTOP, DESKTOP_7K_TEMPLATE],
};

function presetSize(media: MediaManifestItem, key: PresetUrlKey): number | null {
  switch (key) {
    case "s3Url":
      return media.sizeInBytes;
    case "s3UrlPreset2":
      return media.sizeInBytesPreset2;
    case "s3UrlPreset3":
      return media.sizeInBytesPreset3;
    case "s3UrlPreset4":
      return media.sizeInBytesPreset4;
    case "s3UrlImagePreset5":
      return media.sizeInBytesImagePreset5;
    case "s3UrlImagePreset6":
      return media.sizeInBytesImagePreset6;
  }
}

function presetsNeededByRow(media: MediaManifestItem, row: DeviceRow): PresetUrlKey[] {
  const needed = row
    .map((alternatives) => alternatives.find((key) => media[key]))
    .filter((key): key is PresetUrlKey => key !== undefined);
  return needed.length > 0 ? needed : ["s3Url"];
}

export function getMediaFilesToPush(
  media: MediaManifestItem,
  target: MediaPushTarget,
): MediaFileToPush[] {
  const neededKeys = new Set(DEVICE_ROWS[target].flatMap((row) => presetsNeededByRow(media, row)));
  const files = PRESET_ORDER.filter((key) => neededKeys.has(key))
    .map((key) => presetFile(media, media[key], presetSize(media, key)))
    .filter((f): f is MediaFileToPush => f !== null);
  for (const af of media.additionalFiles ?? []) {
    if (!af.s3Url || !af.name) continue;
    files.push({
      url: af.s3Url,
      filename: af.name,
      sizeInBytes: af.sizeInBytes || 0,
      encrypt: false,
    });
  }
  return files;
}

export function getMediaPushSize(media: MediaManifestItem, target: MediaPushTarget): number {
  return getMediaFilesToPush(media, target).reduce((sum, f) => sum + f.sizeInBytes, 0);
}
